import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  makeStyles,
  shorthands,
  Button,
  Input,
  Spinner,
  Combobox,
  Option,
  Text,
  mergeClasses,
} from "@fluentui/react-components";
import { Dismiss16Regular, ArrowUpload24Regular } from "@fluentui/react-icons";
import ComboboxPathOptionText from "./ComboboxPathOptionText";
import FileTypeIcon from "./FileTypeIcon";
import { formatFileSize, MAX_TOTAL_UPLOAD_BYTES } from "../utils/uploadUtils";
import { useComboboxStyles, uploadFolderListboxProps, comboboxPositioning } from "../styles/comboboxStyles";
import { useActionButtonStyles } from "../styles/actionButtonStyles";

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    ...shorthands.gap("10px"),
    ...shorthands.padding("10px"),
    ...shorthands.border("1px", "solid", "#357C7C"),
    ...shorthands.borderRadius("4px"),
    backgroundColor: "#FAFAFA",
  },
  folderRow: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
  },
  folderLabel: {
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333333",
  },
  folderSubheading: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    fontWeight: 400,
    color: "#616161",
  },
  dropZone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.gap("6px"),
    ...shorthands.padding("16px", "12px"),
    ...shorthands.border("1px", "dashed", "#357C7C"),
    ...shorthands.borderRadius("4px"),
    backgroundColor: "#FFFFFF",
    cursor: "pointer",
    textAlign: "center",
    ":hover": {
      backgroundColor: "rgba(53, 124, 124, 0.04)",
    },
  },
  dropZoneActive: {
    backgroundColor: "rgba(53, 124, 124, 0.08)",
  },
  dropZoneIcon: {
    color: "#357C7C",
  },
  dropZoneText: {
    fontFamily: "Segoe UI",
    fontSize: "13px",
    color: "#616161",
  },
  renameHint: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#616161",
  },
  fileList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("6px"),
    maxHeight: "160px",
    overflowY: "auto",
  },
  fileRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("8px"),
    ...shorthands.padding("6px", "8px"),
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    ...shorthands.borderRadius("4px"),
  },
  fileRowError: {
    ...shorthands.borderColor("#C50F1F"),
  },
  fileIconPdf: {
    width: "14px",
    height: "17px",
    flexShrink: 0,
  },
  fileIconWord: {
    width: "18px",
    height: "17px",
    flexShrink: 0,
  },
  fileIconDefault: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
    color: "#569797",
  },
  fileNameInput: {
    flexGrow: 1,
    minWidth: 0,
  },
  fileSize: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#666666",
    flexShrink: 0,
  },
  fileError: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#C50F1F",
    flexBasis: "100%",
    marginTop: "-2px",
    paddingLeft: "30px",
  },
  removeButton: {
    minWidth: "24px",
    ...shorthands.padding("0px"),
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("8px"),
  },
  footerRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap("8px"),
  },
  totalSize: {
    fontFamily: "Segoe UI",
    fontSize: "12px",
    color: "#616161",
  },
  totalSizeError: {
    color: "#C50F1F",
  },
  footerActions: {
    display: "flex",
    flexDirection: "row",
    ...shorthands.gap("8px"),
  },
  statusLine: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("8px"),
    fontFamily: "Segoe UI",
    fontSize: "13px",
    color: "#333333",
  },
  statusError: {
    color: "#C50F1F",
  },
  emptyFolders: {
    fontFamily: "Segoe UI",
    fontSize: "14px",
    color: "#616161",
  },
  hiddenInput: {
    display: "none",
  },
});

const FileUploadPanel = ({
  folders,
  selectedFolderId,
  setSelectedFolderId,
  stagedFiles,
  folderLoading,
  folderError,
  uploading,
  uploadError,
  validation,
  canSubmit,
  onAddFiles,
  onRenameFile,
  onRemoveStagedFile,
  onSubmit,
  onCancel,
  onRetryFolders,
}) => {
  const styles = useStyles();
  const comboboxStyles = useComboboxStyles();
  const actionStyles = useActionButtonStyles();
  const fileInputRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [folderQuery, setFolderQuery] = useState("");
  const [filteredFolders, setFilteredFolders] = useState(folders);

  const selectedFolder = folders.find((folder) => folder.id === selectedFolderId);
  const maxTotalBytes = validation.maxTotalBytes || MAX_TOTAL_UPLOAD_BYTES;
  const totalOverLimit = validation.totalSize > maxTotalBytes;

  useEffect(() => {
    setFilteredFolders(folders);
  }, [folders]);

  useEffect(() => {
    if (selectedFolder) {
      setFolderQuery(selectedFolder.name);
    }
  }, [selectedFolder?.id, selectedFolder?.name]);

  const handleFolderInput = (event) => {
    const value = event.target.value;
    setFolderQuery(value);
    setFilteredFolders(
      folders.filter((folder) => folder.name.toLowerCase().includes(value.toLowerCase()))
    );
  };

  const handleFolderSelect = (_, data) => {
    const id = data.optionValue ? parseInt(data.optionValue, 10) : null;
    setSelectedFolderId(Number.isNaN(id) ? null : id);
    setFolderQuery(data.optionText || "");
    setFilteredFolders(folders);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    if (uploading) {
      return;
    }
    onAddFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!uploading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.folderRow}>
        <Text className={styles.folderLabel}>Upload to a Unicli Synced folder</Text>
        <Text className={styles.folderSubheading}>Files will link immediately after upload</Text>
        {folderLoading ? (
          <div className={styles.statusLine}>
            <Spinner size="tiny" />
            <span>Loading folders…</span>
          </div>
        ) : folderError ? (
          <div className={styles.statusLine}>
            <span className={styles.statusError}>{folderError}</span>
            <Button size="small" appearance="subtle" onClick={onRetryFolders}>
              Retry
            </Button>
          </div>
        ) : folders.length === 0 ? (
          <Text className={styles.emptyFolders}>No folders available for this property.</Text>
        ) : (
          <Combobox
            className={comboboxStyles.dropdownMenuCompose}
            placeholder="Select a folder"
            value={folderQuery}
            onInput={handleFolderInput}
            onChange={(event) => setFolderQuery(event.target.value)}
            onOptionSelect={handleFolderSelect}
            selectedOptions={selectedFolderId != null ? [String(selectedFolderId)] : []}
            disabled={uploading}
            freeform
            positioning={comboboxPositioning}
            listbox={uploadFolderListboxProps(comboboxStyles.dropdownMenuListbox)}
            input={{ className: comboboxStyles.comboBoxInput, autoComplete: "off" }}
            size="small"
          >
            {filteredFolders.map((folder) => (
              <Option key={folder.id} value={String(folder.id)} text={folder.name}>
                <ComboboxPathOptionText
                  name={folder.name}
                  pathLabel="Folder Path"
                  parentPath={folder.parentPath}
                />
              </Option>
            ))}
          </Combobox>
        )}
      </div>

      <div
        className={mergeClasses(styles.dropZone, isDragActive && styles.dropZoneActive)}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (!uploading) {
              fileInputRef.current?.click();
            }
          }
        }}
      >
        <ArrowUpload24Regular className={styles.dropZoneIcon} />
        <Text className={styles.dropZoneText}>Drop files here or click to browse</Text>
        <Text className={styles.dropZoneText}>Max 20 MB per file, 100 MB total</Text>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={styles.hiddenInput}
          onChange={(event) => {
            onAddFiles(event.target.files);
            event.target.value = "";
          }}
          disabled={uploading}
        />
      </div>

      {stagedFiles.length > 0 && (
        <>
          <Text className={styles.renameHint}>You can edit file names before uploading.</Text>
          <div className={styles.fileList}>
            {stagedFiles.map((item) => (
              <div key={item.id}>
                <div className={mergeClasses(styles.fileRow, item.error && styles.fileRowError)}>
                  <FileTypeIcon
                    fileName={item.name}
                    pdfClassName={styles.fileIconPdf}
                    wordClassName={styles.fileIconWord}
                    defaultClassName={styles.fileIconDefault}
                  />
                  <Input
                    className={mergeClasses(comboboxStyles.comboMatchedInput, styles.fileNameInput)}
                    value={item.name}
                    onChange={(_, data) => onRenameFile(item.id, data.value)}
                    disabled={uploading}
                    size="small"
                  />
                  <Text className={styles.fileSize}>{formatFileSize(item.size)}</Text>
                  <Button
                    className={styles.removeButton}
                    appearance="subtle"
                    icon={<Dismiss16Regular />}
                    aria-label={`Remove ${item.name}`}
                    onClick={() => onRemoveStagedFile(item.id)}
                    disabled={uploading}
                  />
                </div>
                {item.error && <Text className={styles.fileError}>{item.error}</Text>}
              </div>
            ))}
          </div>
        </>
      )}

      <div className={styles.footer}>
        {validation.errors.length > 0 && (
          <Text className={styles.statusError}>
            {validation.errors[validation.errors.length - 1]}
          </Text>
        )}
        {uploadError && <Text className={styles.statusError}>{uploadError}</Text>}

        <div className={styles.footerRow}>
          <Text className={mergeClasses(styles.totalSize, totalOverLimit && styles.totalSizeError)}>
            {formatFileSize(validation.totalSize)} / {formatFileSize(maxTotalBytes)}
          </Text>
          <div className={styles.footerActions}>
            <Button
              className={actionStyles.draftButton}
              appearance="outline"
              onClick={onCancel}
              disabled={uploading}
            >
              <span className={actionStyles.draftButtonText}>Cancel</span>
            </Button>
            <Button
              className={actionStyles.draftButton}
              appearance="outline"
              onClick={onSubmit}
              disabled={!canSubmit}
            >
              {uploading ? (
                <Spinner size="tiny" />
              ) : (
                <span className={actionStyles.draftButtonText}>Upload</span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

FileUploadPanel.propTypes = {
  folders: PropTypes.array.isRequired,
  selectedFolderId: PropTypes.number,
  setSelectedFolderId: PropTypes.func.isRequired,
  stagedFiles: PropTypes.array.isRequired,
  folderLoading: PropTypes.bool.isRequired,
  folderError: PropTypes.string,
  uploading: PropTypes.bool.isRequired,
  uploadError: PropTypes.string,
  validation: PropTypes.shape({
    valid: PropTypes.bool.isRequired,
    errors: PropTypes.array.isRequired,
    totalSize: PropTypes.number.isRequired,
    maxTotalBytes: PropTypes.number,
  }).isRequired,
  canSubmit: PropTypes.bool.isRequired,
  onAddFiles: PropTypes.func.isRequired,
  onRenameFile: PropTypes.func.isRequired,
  onRemoveStagedFile: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onRetryFolders: PropTypes.func.isRequired,
};

export default FileUploadPanel;
